// Phase 1A Phase 9C.11 — CouncilShell styles.
//
// Token-only styles for The Council's per-mode shell. Sidebar-free
// 640px-centered dossier framing per UI-architecture §5 (Council's
// sidebarPolicy is "none" in mode-config.ts). Mirrors the BriefShell
// vocabulary so /council reads as part of the same dossier family.
//
// 9C premium rails (cardLow + transitionStandard) applied directly
// here — the file is greenfield, not saturated, so no sibling rails
// file is needed.
//
// Trust contract: no raw hex/rgb, no icons, no emoji, no glow, no
// fake live pulse, no selected-card amber ring.

import type { CSSProperties } from "react";

import { cardLow } from "../../styles/elevation.ts";
import { transitionStandard } from "../../styles/motion.ts";

// ─── Outer article (dossier frame) ───────────────────────────────

export const article: CSSProperties = {
  ...cardLow,
  ...transitionStandard,
  width: "100%",
  maxWidth: "640px",
  margin: "0 auto",
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-6)",
  background: "var(--sz-bg-surface)",
  border: "1px solid var(--sz-border-subtle)",
  borderRadius: "var(--sz-radius-lg)",
  padding: "var(--sz-space-6)",
};

// ─── Header (eyebrow + title + description + back-link) ─────────

export const header: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-3)",
  paddingBottom: "var(--sz-space-4)",
  borderBottom: "1px solid var(--sz-border-subtle)",
};

export const headerLead: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "var(--sz-space-2)",
  color: "var(--sz-text-secondary)",
};

export const eyebrow: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  letterSpacing: "3px",
  textTransform: "uppercase",
  color: "var(--sz-text-secondary)",
};

export const title: CSSProperties = {
  fontFamily: "var(--sz-font-head)",
  fontSize: "var(--sz-head-md)",
  fontWeight: 700,
  color: "var(--sz-text-primary)",
  lineHeight: 1.2,
};

export const description: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-md)",
  color: "var(--sz-text-secondary)",
  lineHeight: 1.6,
};

export const backLink: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
  textDecoration: "none",
  borderBottom: "1px solid var(--sz-border-default)",
  paddingBottom: "2px",
  alignSelf: "flex-start",
};

// ─── Body slot ───────────────────────────────────────────────────

export const body: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-4)",
};

// ─── Card variants used by CouncilPreviewPage ───────────────────

const cardBase: CSSProperties = {
  ...cardLow,
  ...transitionStandard,
  background: "var(--sz-bg-surface)",
  border: "1px solid var(--sz-border-subtle)",
  borderRadius: "var(--sz-radius-lg)",
  padding: "var(--sz-space-5)",
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-3)",
};

export const card: CSSProperties = { ...cardBase };

export const cardEyebrow: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-primary)",
};

export const cardTitle: CSSProperties = {
  fontFamily: "var(--sz-font-head)",
  fontSize: "var(--sz-head-sm)",
  fontWeight: 700,
  color: "var(--sz-text-primary)",
  lineHeight: 1.25,
};

export const cardBody: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-text-secondary)",
  lineHeight: 1.55,
};

export const italicPlaceholder: CSSProperties = {
  fontFamily: "var(--sz-font-head)",
  fontSize: "26px",
  fontWeight: 700,
  fontStyle: "italic",
  color: "var(--sz-text-primary)",
  lineHeight: 1.15,
};

// ─── Future modules list ─────────────────────────────────────────

export const moduleList: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-3)",
  paddingTop: "var(--sz-space-2)",
  borderTop: "1px solid var(--sz-border-subtle)",
};

export const moduleRow: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-1)",
};

export const moduleHead: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: "var(--sz-space-2)",
  flexWrap: "wrap",
};

export const moduleName: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-md)",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  color: "var(--sz-text-primary)",
};

export const moduleHint: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-text-muted)",
  lineHeight: 1.5,
};

// ─── Truth contract list ─────────────────────────────────────────

export const truthList: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-2)",
  margin: 0,
  paddingLeft: "var(--sz-space-4)",
};

export const truthItem: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-text-secondary)",
  lineHeight: 1.55,
};

// ─── Footer safety line ──────────────────────────────────────────

export const safetyLine: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-text-muted)",
  fontStyle: "italic",
  lineHeight: 1.55,
  paddingTop: "var(--sz-space-2)",
};
